/**
 * HumanSummary — the friendly 2-paragraph introduction to a module.
 *
 * Shows up at the top of the technical ModuleDetailPage. Renders the "what is
 * this" + "how is it used" prose for anyone (technical or not) to read at a
 * glance. Designed to be the thing you could copy-paste to an AI and say
 * "I found this — is it useful?".
 *
 * Data source: /feed/module-summaries.json
 *   Extracted from each module's codex.md / recipe.md / module.json by the
 *   build pipeline. Falls back to a friendly synthesis from name + tags when
 *   no prose exists.
 */

import { useEffect, useState } from 'react';
import './HumanSummary.css';

interface ModuleSummary {
  what: string | null;
  usage: string | null;
  name?: string;
  tags?: string[];
  source?: string;
}

type SummaryMap = Record<string, ModuleSummary>;

interface HumanSummaryProps {
  moduleId: string;
  moduleName?: string;
  moduleTags?: string[];
  /**
   * Pre-loaded summary (test override or server-rendered use).
   * If omitted, fetches from /feed/module-summaries.json.
   */
  summary?: ModuleSummary;
}

export function HumanSummary({ moduleId, moduleName, moduleTags, summary }: HumanSummaryProps) {
  const [data, setData] = useState<SummaryMap | null>(summary ? null : null);
  const [loading, setLoading] = useState(!summary);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (summary) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch('/feed/module-summaries.json')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [summary]);

  const entry = summary ?? data?.[moduleId];

  if (loading) {
    return (
      <div className="human-summary human-summary--loading">
        <p>Loading summary…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="human-summary human-summary--error">
        <p>Couldn't load the human summary. The data lives at <code>feed/module-summaries.json</code>.</p>
      </div>
    );
  }

  if (!entry || (!entry.what && !entry.usage)) {
    return null;
  }

  const displayName = moduleName || entry.name || moduleId;

  return (
    <section className="human-summary" aria-label="Human summary">
      <div className="human-summary__lead">
        <span className="human-summary__icon" aria-hidden>📖</span>
        <h2 className="human-summary__heading">What is {displayName}?</h2>
      </div>

      {entry.what && (
        <p className="human-summary__what">{entry.what}</p>
      )}

      {entry.usage && (
        <p className="human-summary__usage">{entry.usage}</p>
      )}

      <div className="human-summary__meta">
        <span className="human-summary__meta-label">Quick take for an AI:</span>
        <code className="human-summary__copyable">
          {displayName} — {entry.what || ''} {entry.usage ? ` ${entry.usage}` : ''}
        </code>
      </div>

      {entry.tags && entry.tags.length > 0 && (
        <div className="human-summary__tags" aria-label="Tags">
          {entry.tags.map((t) => (
            <span key={t} className="human-summary__tag">{t}</span>
          ))}
        </div>
      )}

      {entry.source && entry.source !== 'codex+recipe+module.json' && (
        <p className="human-summary__provenance">
          {entry.source === 'synthesized' 
            ? 'Summary synthesized from module name + tags. Run F6.5 to generate proper prose.'
            : `Source: ${entry.source}`}
        </p>
      )}
    </section>
  );
}
